import React, { useState } from 'react';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import DealNoteEditor from './DealNoteEditor';
import SavedDealNotes from './SavedDealNotes';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'class-names';

const styles = theme => ({
  root: {
    display: 'grid',
    gridTemplateColumns: '1fr',
  },
  rootWithSavedNotes: {
    gridTemplateRows: 'max-content 10px 100px max-content 55px',
    gridTemplateAreas: `
    "header"
    "......"
    "savedNotes"
    "noteEditor"
    "actionButton"
    `,
  },
  rootWithoutSavedNotes: {
    gridTemplateRows: 'max-content 10px max-content 55px',
    gridTemplateAreas: `
    "header"
    "......"
    "noteEditor"
    "actionButton"
    `,
  },
  header: {
    gridArea: 'header',
  },
  savedNotes: {
    gridArea: 'savedNotes',
  },
  noteEditor: {
    gridArea: 'noteEditor',
  },
  actionButton: {
    gridArea: 'actionButton',
    justifySelf: 'end',
    alignSelf: 'end',
  },
  label: {
    gridColumn: 1,
    gridRow: 1,
  },
  charCountLabel: {
    gridColumn: 2,
    gridRow: 1,
    placeSelf: 'end',
    padding: 5,
  },
  inputContainer: {
    height: '38px',
    gridColumn: '1 / -1',
    gridRow: 2,
    width: '100%',
  },
  input: {
    height: '38px',
  },
  error: {
    fontSize: '1rem',
    display: 'block',
  },
});

const maxLength = 1000;

const DealNotes = props => {
  const {
    formData: dealNotes = [],
    classes = {},
    isEditingDealNotes,
    updateEditingDealNoteStatus,
    formError = '',
    onChange: upstreamOnChange,
  } = props;
  const [editableDealNote, setDealNote] = useState('');
  const [editingEnabled, setEditingEnabled] = useState(
    dealNotes.find(({ current }) => current) === undefined,
  );

  if (editableDealNote.length && !isEditingDealNotes)
    updateEditingDealNoteStatus(true);
  if (!editingEnabled && isEditingDealNotes) updateEditingDealNoteStatus(false);

  const onChange = ({ target: { value } = {} }) => {
    if (value.length > maxLength) return;
    setDealNote(value);
  };

  const saveDealNote = () => {
    // Remove current note from list
    const newDealNotes = dealNotes.filter(({ current }) => !current);

    // If current note is entered, add to start of list
    if (editableDealNote.trim()) {
      const newDealNote = { notes: editableDealNote, current: true };
      newDealNotes.unshift(newDealNote);
    }

    // Push change upstream and reset editor state
    upstreamOnChange(newDealNotes);
    setDealNote('');
    setEditingEnabled(false);
  };

  const editDealNote = () => {
    const { notes = '' } = dealNotes.find(({ current }) => current) || {};
    setDealNote(notes);
    setEditingEnabled(true);
  };

  const rootClass = classNames(classes.root, {
    [classes.rootWithSavedNotes]: dealNotes.length > 0,
    [classes.rootWithoutSavedNotes]: dealNotes.length === 0,
  });

  return (
    <div className={rootClass}>
      <Typography className={classes.header} variant="h5">
        Additional Deal Notes
        {!!formError.length && (
          <Typography
            color="error"
            variant="caption"
            className={classes.error}
            data-test-id="DealNotes-error"
          >
            {formError}
          </Typography>
        )}
      </Typography>
      <SavedDealNotes
        dealNotes={dealNotes}
        classes={{ root: classes.savedNotes }}
      />
      <DealNoteEditor
        onChange={onChange}
        notes={editableDealNote}
        maxLength={maxLength}
        classes={{ root: classes.noteEditor }}
        disabled={!editingEnabled}
      />
      {editingEnabled && (
        <Button
          variant="contained"
          size="large"
          color="secondary"
          onClick={saveDealNote}
          className={classes.actionButton}
          data-test-id="DealNotes-saveButton"
        >
          Save Deal Note
        </Button>
      )}
      {!editingEnabled && (
        <Button
          variant="contained"
          size="large"
          color="secondary"
          onClick={editDealNote}
          className={classes.actionButton}
          data-test-id="DealNotes-editButton"
        >
          Edit Deal Note
        </Button>
      )}
    </div>
  );
};

export default withStyles(styles)(DealNotes);
